import { useEffect, useMemo } from 'react';
import { Modal, Form, Select, Checkbox, Tag, Alert, Button, message } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { territoryLabel, territoryOption } from '../../lib/territoryLabel';

interface Props {
  open: boolean;
  onClose: () => void;
  customerType: 'RETAILER' | 'FARMER';
  /** The retailer/farmer being moved — needs at least id, territoryId and a display name. */
  customer: any;
}

/**
 * Move a retailer/farmer to a different territory. The owner recomputes to the new
 * territory's primary SP unless "keep current owner" is ticked. Territory choices are
 * limited to the actor's area (full-access roles get all) by the backend.
 */
export function ChangeTerritoryModal({ open, onClose, customerType, customer }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const name = customer?.companyName ?? customer?.fullName ?? 'customer';

  const { data: territories } = useQuery({
    queryKey: ['my-territories'],
    queryFn: () => request<any[]>({ url: '/territory-assignments/me/territories' }),
    enabled: open,
  });

  const terrMap = useMemo(() => new Map((territories ?? []).map((t: any) => [String(t.id), t])), [territories]);
  // The beat it is in TODAY, drawn exactly like the options below it — code chip, then the place.
  // Never "#126": a database id names nothing a reader can look up.
  const currentLabel = (() => {
    const t: any = terrMap.get(String(customer?.territoryId));
    return t ? territoryLabel(t) : '—';
  })();

  useEffect(() => {
    if (open) form.setFieldsValue({ territoryId: undefined, keepOwner: false });
  }, [open, form]);

  const save = useConfirmedMutation({ title: "Move this customer to the new territory?", content: "The rep who covers that territory takes them over.", okText: "Yes, move" }, {
    mutationFn: (vals: any) => request({
      url: '/territory-assignments/reassign-customer', method: 'POST',
      data: { customerType, customerId: String(customer.id), territoryId: vals.territoryId, keepOwner: !!vals.keepOwner },
    }),
    onSuccess: () => {
      message.success('Territory changed');
      qc.invalidateQueries({ queryKey: ['retailers'] });
      qc.invalidateQueries({ queryKey: ['farmers'] });
      qc.invalidateQueries({ queryKey: ['retailer', String(customer.id)] });
      qc.invalidateQueries({ queryKey: ['territory-coverage'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to change territory'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Change territory · ${name}`}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Change territory</Button></>}
    >
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <div className="mb-3.5 flex flex-wrap items-center gap-2 type-body text-content">Current: {currentLabel}</div>
        <Form.Item name="territoryId" label="New territory" rules={[{ required: true, message: 'Pick the new territory' }]}>
          <Select
            placeholder="Select territory"
            options={(territories ?? []).map((t: any) => ({ value: String(t.id), ...territoryOption(t) }))}
          />
        </Form.Item>
        <Form.Item name="keepOwner" valuePropName="checked"
          extra="By default the owner becomes the new territory's primary Sales Person.">
          <Checkbox label="Keep the current owner (don't reassign)" />
        </Form.Item>
        <Alert type="info"
          title="Open orders, invoices and outstanding stay with the customer — only the territory (and owner) change." />
      </Form>
    </Modal>
  );
}
